// Deploy-site hints (GDD §7, §14): whether one of your standing cities can launch each unit type right now, kept up to
// date from city-change notifications. Each change re-checks only the city that changed against every unit type
// (O(unit types)); a per-type count of launching cities answers the hint in O(1), so no refresh scans the map (a
// World War has 1,271 cities). Local hint only: the sim's commandRejected is the verdict.

import { UNIT_KEYS, typeIdOf, type UnitKey } from '@taxwar/content'
import type { CityIndex } from '@taxwar/net'
import type { MatchView } from '../match/contracts'
import type { SiteState } from './bars'
import { deployObjection } from './rules'
import type { WarMapIndex } from './warMap'

const UNIT_TYPES = UNIT_KEYS.length

export class DeploySites {
  /** Per unit type (UNIT_KEYS order): how many of your cities can launch it. */
  private readonly launchers = new Int32Array(UNIT_TYPES)
  /** Per city × unit type: 1 while that city can launch that unit type. */
  private readonly launches: Uint8Array

  constructor(
    private readonly map: WarMapIndex,
    private readonly match: MatchView,
  ) {
    this.launches = new Uint8Array(map.cities.length * UNIT_TYPES)
    // Once per war: the cities' state at mount (the match may have refreshed them since the war started).
    for (const city of map.cities) this.cityChanged(city.index)
  }

  /** Re-checks one city after it changed; true when some unit type gained its first launch site or lost its last. */
  cityChanged(index: CityIndex): boolean {
    const city = this.map.city(index)
    const state = this.match.cityState(index)
    const base = index * UNIT_TYPES
    let flipped = false
    for (let unit = 0; unit < UNIT_TYPES; unit++) {
      const launches = deployObjection(UNIT_KEYS[unit] as UnitKey, city, state, this.map.you) === null ? 1 : 0
      const before = this.launches[base + unit] as number
      if (launches === before) continue
      this.launches[base + unit] = launches
      const previous = this.launchers[unit] as number
      const count = previous + launches - before
      this.launchers[unit] = count
      // A count moving between 0 and 1 is the only change the hint shows.
      if (previous === 0 || count === 0) flipped = true
    }
    return flipped
  }

  siteState(unit: UnitKey): SiteState {
    return (this.launchers[typeIdOf(UNIT_KEYS, unit)] as number) > 0 ? 'met' : 'unmet'
  }
}
